import { supabase } from "@/integrations/supabase/client";

/**
 * Centralized storage upload helper.
 *
 * Every user-facing upload in the app should go through here so that:
 *  - file size / mime type are validated before hitting the network,
 *  - paths are always scoped correctly for the bucket's security rules,
 *  - permission failures surface a human-readable message.
 */

export type UploadBucket =
  | "profile-avatars"
  | "kyc-documents"
  | "p2p-proofs"
  | "support-attachments"
  | "name-change-proofs"
  | "marketer-proofs"
  | "staking-images"
  | "listing-documents";

interface BucketRule {
  /** Max file size in bytes */
  maxBytes: number;
  /** Allowed mime types; `*` allows any image */
  mimeTypes: string[];
  /** Public bucket -> we can build a public URL */
  isPublic: boolean;
  /** Uploads must live under `<user-id>/...` */
  userScoped: boolean;
}

const MB = 1024 * 1024;

const IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const MEDIA_IMAGE_TYPES = [...IMAGE_TYPES, "image/gif"];
const DOC_TYPES = [...IMAGE_TYPES, "application/pdf"];

export const BUCKET_RULES: Record<UploadBucket, BucketRule> = {
  "profile-avatars": { maxBytes: 2 * MB, mimeTypes: IMAGE_TYPES, isPublic: true, userScoped: true },
  "kyc-documents": { maxBytes: 10 * MB, mimeTypes: DOC_TYPES, isPublic: false, userScoped: true },
  "p2p-proofs": { maxBytes: 10 * MB, mimeTypes: DOC_TYPES, isPublic: false, userScoped: true },
  "support-attachments": { maxBytes: 10 * MB, mimeTypes: DOC_TYPES, isPublic: false, userScoped: true },
  "name-change-proofs": { maxBytes: 10 * MB, mimeTypes: DOC_TYPES, isPublic: false, userScoped: true },
  "marketer-proofs": { maxBytes: 10 * MB, mimeTypes: DOC_TYPES, isPublic: false, userScoped: true },
  // listing-documents is written via signed URLs issued by the listing-application edge function
  "listing-documents": { maxBytes: 10 * MB, mimeTypes: DOC_TYPES, isPublic: false, userScoped: false },
  // staking-images also holds NFT media, which is folder-scoped rather than user-scoped
  "staking-images": {
    maxBytes: 25 * MB,
    mimeTypes: [...MEDIA_IMAGE_TYPES, "video/mp4", "video/webm", "audio/mpeg"],
    isPublic: true,
    userScoped: false,
  },
};

export interface UploadResult {
  /** Path inside the bucket */
  path: string;
  /** Public URL (public buckets) or signed URL (private buckets) */
  url: string;
}

export interface UploadOptions {
  bucket: UploadBucket;
  file: File;
  /**
   * Path *inside* the user's folder for user-scoped buckets
   * (e.g. `disputes/<id>`), or the full path prefix for non user-scoped buckets.
   */
  prefix?: string;
  /** Overrides the generated filename (extension is preserved when omitted). */
  fileName?: string;
  /** Required for user-scoped buckets. Falls back to the current session user. */
  userId?: string;
  /** Seconds a signed URL stays valid for private buckets. Default 1 hour. */
  signedUrlTtl?: number;
  upsert?: boolean;
}

export class UploadError extends Error {}

function extOf(file: File) {
  const fromName = file.name.split(".").pop()?.toLowerCase();
  if (fromName && /^[a-z0-9]{1,5}$/.test(fromName)) return fromName;
  return file.type.split("/").pop()?.toLowerCase() || "bin";
}

function humanSize(bytes: number) {
  return bytes >= MB ? `${Math.round(bytes / MB)}MB` : `${Math.round(bytes / 1024)}KB`;
}

/** Validates a file against the bucket rules. Returns an error message, or null when valid. */
export function validateFile(bucket: UploadBucket, file: File): string | null {
  const rule = BUCKET_RULES[bucket];
  if (file.size > rule.maxBytes) {
    return `File is too large. Maximum size is ${humanSize(rule.maxBytes)}.`;
  }
  if (file.size === 0) return "File appears to be empty.";
  const type = (file.type || "").toLowerCase();
  const isHeic = /hei[cf]/.test(type) || /\.hei[cf]$/i.test(file.name);
  if (isHeic && !rule.mimeTypes.includes(type)) {
    return "HEIC photos are not supported. On iPhone, choose \"Most Compatible\" or convert the photo to JPG or PNG.";
  }
  if (type && !rule.mimeTypes.includes(type)) {
    const pretty = rule.mimeTypes.map((m) => m.split("/")[1].toUpperCase());
    return `Unsupported file type. Allowed: ${[...new Set(pretty)].join(", ")}.`;
  }
  return null;
}

/** True when the storage failure is an authentication problem we can retry after a refresh. */
function isAuthError(error: { message?: string; statusCode?: string; error?: string } | null): boolean {
  const text = `${error?.message ?? ""} ${(error as any)?.error ?? ""}`;
  return /invalid token|invalid jwt|invalidjwt|jwt expired|token is expired|missing sub claim|invalid signature|invalid claim|401/i.test(text);
}

/** Turns a raw storage error into something a user can act on. */
export function friendlyStorageError(error: { message?: string; statusCode?: string } | null): string {
  const msg = error?.message ?? "";
  if (isAuthError(error)) {
    return "Your session expired. Please sign in again and retry the upload.";
  }
  if (/row-level security|violates row level|Unauthorized|403/i.test(msg)) {
    return "You don't have permission to upload here. Please sign in again and retry.";
  }
  if (/exceeded the maximum allowed size|Payload too large|413/i.test(msg)) {
    return "File is too large for this upload.";
  }
  if (/mime type|not supported/i.test(msg)) {
    return "This file type is not allowed.";
  }
  if (/already exists|Duplicate/i.test(msg)) {
    return "A file with this name already exists. Please try again.";
  }
  return msg || "Upload failed. Please try again.";
}

/** Refreshes the access token when it is missing or about to expire. */
async function ensureFreshSession(): Promise<string | undefined> {
  const { data } = await supabase.auth.getSession();
  const session = data.session;
  if (!session) return undefined;
  const expiresAt = (session.expires_at ?? 0) * 1000;
  if (expiresAt && expiresAt - Date.now() > 120_000) return session.user.id;
  const { data: refreshed } = await supabase.auth.refreshSession();
  return refreshed.session?.user.id ?? session.user.id;
}

function impersonationActive(): boolean {
  try {
    return !!sessionStorage.getItem("acex_impersonation");
  } catch {
    return false;
  }
}

export async function uploadToBucket(options: UploadOptions): Promise<UploadResult> {
  const { bucket, file, prefix, fileName, signedUrlTtl = 3600, upsert = false } = options;
  const rule = BUCKET_RULES[bucket];

  const invalid = validateFile(bucket, file);
  if (invalid) throw new UploadError(invalid);

  if (impersonationActive()) {
    throw new UploadError("Uploads are disabled while viewing an account as staff.");
  }

  const sessionUserId = await ensureFreshSession();

  let userId = options.userId ?? sessionUserId;
  if (rule.userScoped && !userId) {
    throw new UploadError("You must be signed in to upload files.");
  }

  const name = fileName ?? `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${extOf(file)}`;
  const segments = [
    rule.userScoped ? userId : undefined,
    prefix?.replace(/^\/+|\/+$/g, "") || undefined,
    name,
  ].filter(Boolean) as string[];
  const path = segments.join("/");

  const doUpload = () =>
    supabase.storage.from(bucket).upload(path, file, { upsert, contentType: file.type || undefined });

  let { error } = await doUpload();

  // One retry after a forced token refresh — covers tokens that expired mid-flight.
  if (error && isAuthError(error as any)) {
    const { error: refreshErr } = await supabase.auth.refreshSession();
    if (!refreshErr) {
      ({ error } = await doUpload());
    }
  }

  if (error) throw new UploadError(friendlyStorageError(error as any));

  if (rule.isPublic) {
    const { data } = supabase.storage.from(bucket).getPublicUrl(path);
    return { path, url: data.publicUrl };
  }

  const { data } = await supabase.storage.from(bucket).createSignedUrl(path, signedUrlTtl);
  return { path, url: data?.signedUrl ?? path };
}

/** Best-effort delete; never throws. */
export async function removeFromBucket(bucket: UploadBucket, paths: string[]) {
  if (!paths.length) return;
  try {
    await supabase.storage.from(bucket).remove(paths);
  } catch {
    /* non-fatal */
  }
}
